import { ChangeEvent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeKnmFileSizeErrorBig } from 'CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorBig';
import { IconFile } from 'UIPackage/IconsModule/components/IconFile';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';

import styles from './UploadModal.module.scss';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';

interface IUploadModalProps {
  visible: boolean;
  modalTitle: string;
  requireSign?: boolean;
  accept: string;
  maxFileSize: number;
  uploadButtonTitle?: string;
  onClose: () => void;
  onSave: (document: File, sign: File | null) => void;
  validateDocument?: (file: File) => boolean;
  fileFormatErrorText?: string;
  clearOnClose?: boolean;
}

export const UploadModal = ({
  visible,
  modalTitle,
  requireSign,
  accept,
  maxFileSize,
  uploadButtonTitle = 'Загрузить',
  onSave,
  onClose,
  validateDocument,
  fileFormatErrorText,
  clearOnClose = false,
}: IUploadModalProps) => {
  const [document, setDocument] = useState<File | null>(null);
  const [sign, setSign] = useState<File | null>(null);

  const dispatch = useDispatch<Dispatch<RootActions>>();

  const resetModalState = useCallback(() => {
    setDocument(null);
    setSign(null);
  }, []);

  const handleClose = useCallback(() => {
    if (clearOnClose) {
      resetModalState();
    }

    onClose();
  }, [clearOnClose, onClose, resetModalState]);

  const handleAfterClose = useCallback(() => {
    resetModalState();
  }, [resetModalState]);

  const handleSaveModalButton = useCallback(() => {
    if (document && !(requireSign && !sign)) {
      onSave(document, sign);
    }
    handleClose();
  }, [document, handleClose, onSave, requireSign, sign]);

  const handleRemoveDocument = () => {
    setDocument(null);
  };

  const handleRemoveSign = () => {
    setSign(null);
  };

  const handleSetAttachment = useCallback((file: File, isSign: boolean) => {
    isSign ? setSign(file) : setDocument(file);
  }, []);

  const renderAttachment = (attachment: File, isSign?: boolean) => (
    <div className={styles.AttachedDocument} key={attachment.name}>
      <IconFile width="small" fileName={attachment.name} />
      <span className={styles.FileName}>{attachment.name}</span>
      <button className={styles.RemoveButton} type="button" onClick={isSign ? handleRemoveSign : handleRemoveDocument}>
        x
      </button>
    </div>
  );

  const handleInputAttachment = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const file = e.target.files[0];
        const isSizeValid = file.size <= maxFileSize;
        const isSign = file.name.split('.').pop() === 'sig';

        if (!isSizeValid) {
          dispatch(addNoticeKnmFileSizeErrorBig());
        } else if (!isSign && fileFormatErrorText && validateDocument && !validateDocument(file)) {
          dispatch(
            addNoticeAction({
              renderContent: fileFormatErrorText,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.UPLOAD_DOCUMENT,
            }),
          );
        } else {
          handleSetAttachment(file, isSign);
        }
      }
      e.target.value = '';
    },
    [dispatch, handleSetAttachment, maxFileSize, validateDocument, fileFormatErrorText],
  );

  return (
    <Modal modalBodyClassName={styles.ModalBody} visible={visible} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader title={modalTitle} color="black" sizeText="small" />
      <ModalBody size="content" border="bottom">
        <div className={styles.InputBlock}>
          <div className={styles.InputContainer}>
            <div className={styles.Attachments}>
              <div className={styles.AttachmentsContainer}>
                <div className={styles.AttachmentHeader}>
                  <span>Документ</span>
                  <label className={styles.AddAttachmentLabel} htmlFor="document">
                    Добавить
                  </label>
                  <input
                    id="document"
                    name="document"
                    type="file"
                    accept={accept}
                    style={{ display: 'none' }}
                    onChange={handleInputAttachment}
                  />
                </div>
                {document && renderAttachment(document)}
              </div>
              <div className={styles.AttachmentsContainer}>
                <div className={styles.AttachmentHeader}>
                  <span>Подпись</span>
                  <label className={styles.AddAttachmentLabel} htmlFor="signs">
                    Добавить
                  </label>
                  <input
                    id="signs"
                    name="signs"
                    type="file"
                    accept=".sig"
                    style={{ display: 'none' }}
                    onChange={handleInputAttachment}
                  />
                </div>
                {sign && renderAttachment(sign, true)}
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalActions>
        <Button text="Закрыть" size="medium" onClick={handleClose} />
        <Button
          text={uploadButtonTitle}
          id="uploadButton"
          size="medium"
          disabled={!document || (requireSign && !sign)}
          onClick={handleSaveModalButton}
        />
      </ModalActions>
    </Modal>
  );
};
